import {useState} from 'react'

import {difference, isEqual, keyBy, noop, pick, uniqBy} from 'es-toolkit'
import {get, isEmpty, some} from 'es-toolkit/compat'
import {useFormik} from 'formik'
import invariant from 'invariant'
import * as Yup from 'yup'

import config from '../../../config'

import {useAppDispatch, useAppSelector} from '../../../hooks/redux'
import {closeModal} from '../../../redux/features/modal'

import preventDoubleClick from '../../../../../commons/src/services/prevent-double-click'
import * as providerEmailApi from '../../../services/api/provider-email'
import {captureException} from '../../../services/logging'

import {ModalMeta, ProviderEmail} from '../../../types'

import emailIcon from '../../../../../commons/src/assets/icons/email.svg'
import plusIcon from '../../../../../commons/src/assets/icons/plus.svg'

import ActionBar from '../../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../../commons/src/components/button'
import FieldNote from '../../../../../commons/src/components/field-note'
import FormRow from '../../../../../commons/src/components/form-row'
import Headline from '../../../../../commons/src/components/headline'
import Icon from '../../../../../commons/src/components/icon'
import InputField from '../../../../../commons/src/components/input-field'
import LabeledCheckbox from '../../../../../commons/src/components/labeled-checkbox-field'
import Modal from '../../../../../commons/src/components/modal'
import Paragraph from '../../../../../commons/src/components/paragraph'
import Repeatable from '../../../../../commons/src/components/repeatable'
import Stack from '../../../../../commons/src/components/stack'

const initialContactValues = {
  email: '',
  invoices: false,
  messages: false,
  news: false,
  orders: false,
}

const initialValues = {contacts: [initialContactValues], formikGeneral: ''}

const getInitialProviderEmail = (index: number) => ({
  email: '',
  invoices: index === 0 ? true : false,
  messages: index === 0 ? true : false,
  orders: index === 0 ? true : false,
  news: false,
})

const validationSchema = {
  contacts: Yup.array()
    .of(
      Yup.object({
        email: Yup.string().email().label('Contact email').required(),
        invoices: Yup.boolean().required(),
        messages: Yup.boolean().required(),
        orders: Yup.boolean().required(),
        news: Yup.boolean().required(),
      }).test(
        'AtLeastOneCheck',
        'You need to select at least one category per contact email.',
        (contact, ctx) => {
          const foo = Object.values(pick(contact, ['invoices', 'orders', 'news', 'messages']))
          if (some(foo, Boolean)) {
            return true
          }
          return ctx.createError({
            path: `formikGeneral`,
          })
        }
      )
    )
    .test('UniqueEmails', 'Contact emails need te be unique.', (contacts, ctx) => {
      if (contacts) {
        const uniqueData = uniqBy(contacts, contact => contact.email)

        const isUnique = (contacts && contacts.length) === uniqueData.length
        if (isUnique) {
          return true
        }
        const nonUniqueIndex = contacts.findIndex(
          (row, i) =>
            (!!row && row.email?.toLowerCase()) !== (uniqueData[i] ? uniqueData[i].email : '')
        )
        if (contacts[nonUniqueIndex].email === '') {
          return true
        }
        return ctx.createError({
          path: `${ctx.path}.${nonUniqueIndex}.${'email'}`,
        })
      }
      return true
    })
    .test(
      'AtLeastOneContactForOrdersAndInvoices',
      'You need at least one contact for "orders", "messages" and "invoices".',
      (contacts, ctx) => {
        if (
          contacts &&
          contacts.filter(contact => contact.orders).length > 0 &&
          contacts.filter(contact => contact.messages).length > 0 &&
          contacts.filter(contact => contact.invoices).length > 0
        ) {
          return true
        }
        return ctx.createError({
          path: `formikGeneral`,
        })
      }
    ),
}

const errorMessage = 'Oops we could not save all your changes. Please try again!'

type Props = {
  meta: ModalMeta
  providerEmails: ProviderEmail[]
  onDone?: () => void
}

const EditProviderEmailsModal = ({meta, providerEmails, onDone = noop}: Props) => {
  const [apiFailed, setApiFailed] = useState(false)

  const dispatch = useAppDispatch()
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      ...{contacts: providerEmails},
    },
    validationSchema: Yup.object(validationSchema),
    onSubmit: async (values, actions) => {
      const providerEmailsById = keyBy(providerEmails, providerEmail => providerEmail.id)
      const contactsWithId = values.contacts.filter(contact => !!contact.id)

      const idsToDelete = difference(
        providerEmails.map(c => c.id),
        contactsWithId.map(c => c.id)
      )

      const contactsToUpdate = contactsWithId.filter(c => !isEqual(c, providerEmailsById[c.id]))
      const idsToUpdate = contactsToUpdate.map(c => c.id)

      const contactsToCreate = values.contacts
        .filter(contact => !contact.id)
        .map(c => ({...c, provider: provider.id}))

      try {
        await Promise.all([
          providerEmailApi.removeMultiple(idsToDelete),
          providerEmailApi.updateMultiple(
            keyBy(contactsToUpdate, contactToUpdate => contactToUpdate.id),
            idsToUpdate
          ),
          providerEmailApi.createMultiple(contactsToCreate),
        ])

        onDone()
        dispatch(closeModal())
      } catch (error) {
        captureException(error)
        actions.setFieldError('formikGeneral', errorMessage)
        setApiFailed(true)
      }
    },
  })

  return (
    <Modal
      size="xl"
      fullscreenMobile={meta.fullscreenMobile}
      headline={<Headline label="Edit your contact emails" size="l" />}
      onClose={() => dispatch(closeModal())}
      buttons={
        <>
          {!apiFailed && (
            <>
              <Button hierarchy="ghost" onClick={() => dispatch(closeModal())}>
                Cancel
              </Button>
              <Button
                disabled={
                  formik.isSubmitting || (formik.submitCount > 0 && !isEmpty(formik.errors))
                }
                onClick={preventDoubleClick(() => formik.handleSubmit())}
              >
                Save
              </Button>
            </>
          )}
          {apiFailed && (
            <Button
              disabled={formik.isSubmitting}
              onClick={preventDoubleClick(() => {
                // Trigger refetch to get changed data
                onDone()
                dispatch(closeModal())
              })}
            >
              OK
            </Button>
          )}
        </>
      }
    >
      <Stack space="300">
        <form onSubmit={formik.handleSubmit}>
          <Repeatable
            min={1}
            button={
              <Button
                hierarchy="secondary"
                disabled={formik.isSubmitting || apiFailed}
                icon={<Icon source={plusIcon} />}
              >
                Add contact
              </Button>
            }
            onAdd={nextIndex => {
              formik.setFieldValue('contacts', [
                ...formik.values.contacts,
                getInitialProviderEmail(nextIndex),
              ])
            }}
            onDelete={index => {
              formik.setFieldValue(
                'contacts',
                formik.values.contacts.filter((_, i) => i !== index)
              )
            }}
          >
            {formik.values.contacts.map((contact, index) => (
              <FieldNote
                key={index}
                appearance="error"
                note={formik.submitCount > 0 && get(formik.errors, `contacts[${index}].email`)}
                className="u-margin-bottom"
              >
                <FormRow layout="half-half">
                  <InputField
                    placeholder="Email"
                    name={`contacts[${index}].email`}
                    value={contact.email}
                    appearance={
                      formik.submitCount > 0 && !!get(formik.errors, `contacts[${index}].email`)
                        ? 'error'
                        : undefined
                    }
                    icon={<Icon source={emailIcon} />}
                    onChange={(value, name) => formik.setFieldValue(name as string, value)}
                    onBlur={formik.handleBlur}
                    disabled={formik.isSubmitting || apiFailed}
                  />

                  <ActionBar noWrap className="u-margin-right-l">
                    <LabeledCheckbox
                      label="Orders"
                      name={`contacts[${index}].orders`}
                      checked={contact.orders}
                      onChange={(value, name) => formik.setFieldValue(name as string, value)}
                      onBlur={formik.handleBlur}
                      appearance={
                        formik.submitCount > 0 && !!get(formik.errors, `contacts[${index}].orders`)
                          ? 'error'
                          : undefined
                      }
                      disabled={formik.isSubmitting || apiFailed}
                    />
                    <LabeledCheckbox
                      label="Invoices"
                      name={`contacts[${index}].invoices`}
                      checked={contact.invoices}
                      onChange={(value, name) => formik.setFieldValue(name as string, value)}
                      onBlur={formik.handleBlur}
                      appearance={
                        formik.submitCount > 0 &&
                        !!get(formik.errors, `contacts[${index}].invoices`)
                          ? 'error'
                          : undefined
                      }
                      disabled={formik.isSubmitting || apiFailed}
                    />
                    <LabeledCheckbox
                      label="Messages"
                      name={`contacts[${index}].messages`}
                      checked={contact.messages}
                      onChange={(value, name) => formik.setFieldValue(name as string, value)}
                      onBlur={formik.handleBlur}
                      appearance={
                        formik.submitCount > 0 &&
                        !!get(formik.errors, `contacts[${index}].messages`)
                          ? 'error'
                          : undefined
                      }
                      disabled={formik.isSubmitting || apiFailed}
                    />
                    <LabeledCheckbox
                      label="News"
                      name={`contacts[${index}].news`}
                      checked={contact.news}
                      onChange={(value, name) => formik.setFieldValue(name as string, value)}
                      onBlur={formik.handleBlur}
                      appearance={
                        formik.submitCount > 0 && !!get(formik.errors, `contacts[${index}].news`)
                          ? 'error'
                          : undefined
                      }
                      disabled={formik.isSubmitting || apiFailed}
                    />
                  </ActionBar>
                </FormRow>
              </FieldNote>
            ))}
          </Repeatable>
        </form>
        {formik.submitCount > 0 && formik.errors.formikGeneral && (
          <Paragraph error>{formik.errors.formikGeneral}</Paragraph>
        )}
        <Stack space="80">
          <Paragraph minor size="s" className="u-no-margin-bottom">
            <strong>Invoices:</strong> {config.providerEmail.invoiceDescription}
          </Paragraph>
          <Paragraph minor size="s" className="u-no-margin-bottom">
            <strong>Orders:</strong> {config.providerEmail.ordersDescription}
          </Paragraph>
          <Paragraph minor size="s" className="u-no-margin-bottom">
            <strong>Messages:</strong> {config.providerEmail.messagesDescription}
          </Paragraph>
          <Paragraph minor size="s" className="u-no-margin-bottom">
            <strong>News:</strong> {config.providerEmail.newsDescription}
          </Paragraph>
        </Stack>
      </Stack>
    </Modal>
  )
}

export default EditProviderEmailsModal
